<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useSidebarContext } from './provider';
import Icon from 'next/icon/Icon.vue';
import SidebarUnreadBadge from './SidebarUnreadBadge.vue';
import {
  ROSH_ITEM_BASE,
  ROSH_ITEM_ACTIVE,
  roshItemSize,
} from './roshItemClasses';

// Rosh whitelabel: os filtros de conversa mais usados ficam numa seção plana
// própria — "Não atendidas" primeiro (é o que gera urgência de SLA) — em vez
// de aninhados/colapsados dentro de um grupo "Conversas".
const { t } = useI18n();
const { accountScopedRoute } = useAccount();
const { isCollapsed } = useSidebarContext();

const allUnreadCount = useMapGetter('conversationUnreadCounts/getAllUnreadCount');
const myInboxUnreadCount = useMapGetter('notifications/getUnreadCount');

const items = computed(() => [
  {
    key: 'unattended',
    label: t('SIDEBAR.UNATTENDED_CONVERSATIONS'),
    icon: 'i-lucide-clock-alert',
    iconClass: 'text-n-amber-9',
    to: accountScopedRoute('conversation_unattended'),
    variant: 'attention',
  },
  {
    key: 'all',
    label: t('SIDEBAR.ALL_CONVERSATIONS'),
    icon: 'i-lucide-inbox',
    to: accountScopedRoute('home'),
    badge: allUnreadCount.value,
  },
  {
    key: 'mentions',
    label: t('SIDEBAR.MENTIONED_CONVERSATIONS'),
    icon: 'i-lucide-at-sign',
    to: accountScopedRoute('conversation_mentions'),
  },
  {
    key: 'mine',
    label: t('SIDEBAR.INBOX'),
    icon: 'i-lucide-user-round-check',
    to: accountScopedRoute('inbox_view'),
    badge: myInboxUnreadCount.value,
  },
]);
</script>

<template>
  <div
    class="flex flex-col gap-1 min-w-0"
    :class="isCollapsed ? 'items-center' : ''"
  >
    <span
      v-if="!isCollapsed"
      class="px-2 text-[11px] font-semibold tracking-wider uppercase truncate text-n-slate-10"
    >
      {{ t('SIDEBAR.CONVERSATIONS') }}
    </span>
    <RouterLink
      v-for="item in items"
      :key="item.key"
      :to="item.to"
      :title="item.label"
      :active-class="ROSH_ITEM_ACTIVE"
      :class="[ROSH_ITEM_BASE, roshItemSize(isCollapsed)]"
    >
      <Icon :icon="item.icon" class="flex-shrink-0 size-4" :class="item.iconClass" />
      <span v-if="!isCollapsed" class="flex-1 text-sm truncate text-start">
        {{ item.label }}
      </span>
      <SidebarUnreadBadge
        v-if="!isCollapsed && item.badge"
        :count="item.badge"
        :variant="item.variant || 'unread'"
      />
    </RouterLink>
  </div>
</template>
